import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, MailCheck, RefreshCw, XCircle, ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VerifyEmail() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState(params.get("email") || "");
  const [token, setToken] = useState(params.get("token") || "");
  const [state, setState] = useState("idle"); // idle | verifying | success | error
  const [errorMsg, setErrorMsg] = useState("");
  const [resending, setResending] = useState(false);
  const attemptedRef = useRef(false);

  const doVerify = useCallback(async (emailArg, tokenArg) => {
    setState("verifying");
    setErrorMsg("");
    try {
      const r = await axios.post(`${API}/public/verify-email`, {
        email: emailArg, token: tokenArg,
      });
      if (r.data?.already_verified) {
        toast.success("Email already verified. Waiting on admin review.");
      } else {
        toast.success("Email verified. MIRO Support will review shortly.");
      }
      setState("success");
    } catch (err) {
      setErrorMsg(err?.response?.data?.detail || "Verification failed. The link may have expired.");
      setState("error");
    }
  }, []);

  // Auto-verify if both params were in the URL (runs once on mount).
  useEffect(() => {
    if (attemptedRef.current) return;
    if (email && token) {
      attemptedRef.current = true;
      doVerify(email, token);
    }
  }, [email, token, doVerify]);

  const submit = (e) => {
    e.preventDefault();
    if (!email.trim() || !token.trim()) return toast.error("Enter email and code");
    doVerify(email.trim(), token.trim());
  };

  const resend = async () => {
    if (!email.trim()) return toast.error("Enter your email first");
    setResending(true);
    try {
      await axios.post(`${API}/public/resend-verification`, { email: email.trim() });
      toast.success("If your request is on file, a fresh verification email has been sent.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not resend.");
    } finally { setResending(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6"
         style={{ background: "var(--miro-bg, #F5F7FB)" }}>
      <div className="max-w-md w-full bg-white border border-[var(--miro-border)] rounded-2xl p-8">
        <Link to="/login" data-testid="verify-back-link"
              className="inline-flex items-center gap-1 text-xs text-[var(--miro-text-muted)] hover:text-[var(--miro-navy)]">
          <ArrowLeft size={13}/> Back to sign in
        </Link>

        {state === "success" ? (
          <div className="text-center py-4" data-testid="verify-email-success">
            <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center bg-green-500">
              <CheckCircle2 className="text-white" size={26}/>
            </div>
            <h1 className="font-display font-black text-2xl text-[var(--miro-navy)]">Email verified</h1>
            <p className="text-sm text-[var(--miro-text-secondary)] mt-3">
              Thanks! MIRO Support has been notified and will review your
              request shortly. You'll receive another email once your account
              is activated.
            </p>
            <Button
              data-testid="verify-return-btn"
              onClick={() => nav("/login")}
              className="mt-6 w-full h-11 font-semibold"
              style={{ background: "var(--miro-navy)" }}
            >
              Back to sign in
            </Button>
          </div>
        ) : state === "error" ? (
          <div data-testid="verify-email-error">
            <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center bg-red-500">
              <XCircle className="text-white" size={26}/>
            </div>
            <h1 className="font-display font-black text-2xl text-[var(--miro-navy)] text-center">
              Verification failed
            </h1>
            <p className="text-sm text-red-600 mt-3 text-center">{errorMsg}</p>
            <Button
              data-testid="verify-resend-btn"
              onClick={resend}
              disabled={resending || !email.trim()}
              className="mt-6 w-full h-11 font-semibold"
              style={{ background: "var(--miro-navy)" }}
            >
              <RefreshCw size={15} className="mr-1.5"/>
              {resending ? "Resending…" : "Send a new verification link"}
            </Button>
          </div>
        ) : (
          <>
            <div className="miro-overline mt-4">Verify your email</div>
            <h1 className="font-display font-black text-3xl tracking-tight text-[var(--miro-navy)]">
              Confirm your email address
            </h1>
            <p className="text-sm text-[var(--miro-text-secondary)] mt-2">
              Enter the details from the email we sent you. Verification links
              expire after <strong>30 minutes</strong>.
            </p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <Label>Work email</Label>
                <Input data-testid="verify-email-input" required type="email" className="mt-1"
                       value={email} onChange={(e) => setEmail(e.target.value)}/>
              </div>
              <div>
                <Label>Verification code</Label>
                <Input data-testid="verify-token-input" required className="mt-1"
                       value={token} onChange={(e) => setToken(e.target.value)}
                       placeholder="Paste the code from the email"/>
              </div>
              <Button
                data-testid="verify-submit-btn"
                type="submit" disabled={state === "verifying"}
                className="w-full h-11 font-semibold"
                style={{ background: "var(--miro-navy)" }}
              >
                {state === "verifying" ? "Verifying…" : (<><MailCheck size={15} className="mr-1.5"/> Verify email</>)}
              </Button>
              <Button type="button" data-testid="verify-resend-btn-alt" variant="outline"
                      onClick={resend} disabled={resending || !email.trim()}
                      className="w-full h-11 font-semibold">
                {resending ? "Resending…" : "Resend verification email"}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
